/**
 * Settings Component
 * User settings for timezone and notifications
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../api';
import { useTelegramApp } from '../../hooks';
import type { UserSettings, TimezoneOption, SubscriptionUpdate } from '../../types';
import styles from './Settings.module.css';

interface SettingsProps {
  onBack: () => void;
}

// Retrograde planets configuration
const RETROGRADE_PLANETS = [
  { key: 'mercury', name: 'Меркурий', symbol: '☿' },
  { key: 'venus', name: 'Венера', symbol: '♀' },
  { key: 'mars', name: 'Марс', symbol: '♂' },
  { key: 'jupiter', name: 'Юпитер', symbol: '♃' },
  { key: 'saturn', name: 'Сатурн', symbol: '♄' },
];

// Eclipse types configuration
const ECLIPSE_TYPES = [
  { key: 'solar_eclipse', name: 'Солнечные затмения', symbol: '☀' },
  { key: 'lunar_eclipse', name: 'Лунные затмения', symbol: '☽' },
];

export function Settings({ onBack }: SettingsProps) {
  const { haptic, showBackButton, hideBackButton } = useTelegramApp();
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [timezones, setTimezones] = useState<TimezoneOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retrogradesExpanded, setRetrogradesExpanded] = useState(false);
  const [eclipsesExpanded, setEclipsesExpanded] = useState(false);

  // Fetch settings and timezones on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [settingsRes, timezonesRes] = await Promise.all([
          api.getSettings(),
          api.getTimezones(),
        ]);

        if (settingsRes.success && settingsRes.settings) {
          setSettings(settingsRes.settings);
        } else {
          setError(settingsRes.error || 'Failed to load settings');
        }

        if (timezonesRes.success) {
          setTimezones(timezonesRes.timezones);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Connection error');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Setup back button
  useEffect(() => {
    showBackButton(onBack);
    return () => hideBackButton();
  }, [showBackButton, hideBackButton, onBack]);

  // Build a map of enabled subscriptions
  const enabledSubscriptions = useMemo(() => {
    if (!settings?.subscriptions) return new Set<string>();
    return new Set(
      settings.subscriptions
        .filter((s) => s.enabled)
        .map((s) => s.eventType)
    );
  }, [settings?.subscriptions]);

  // Check if a retrograde planet is subscribed (both start and end events)
  const isPlanetSubscribed = useCallback(
    (planetKey: string) => {
      const startEvent = `${planetKey}_retrograde_start`;
      const endEvent = `${planetKey}_retrograde_end`;
      return enabledSubscriptions.has(startEvent) && enabledSubscriptions.has(endEvent);
    },
    [enabledSubscriptions]
  );

  // Check if an eclipse type is subscribed
  const isEclipseSubscribed = useCallback(
    (eclipseKey: string) => {
      return enabledSubscriptions.has(eclipseKey);
    },
    [enabledSubscriptions]
  );

  // Count enabled retrogrades and eclipses
  const retrogradesCount = useMemo(() => {
    return RETROGRADE_PLANETS.filter((p) => isPlanetSubscribed(p.key)).length;
  }, [isPlanetSubscribed]);

  const eclipsesCount = useMemo(() => {
    return ECLIPSE_TYPES.filter((e) => isEclipseSubscribed(e.key)).length;
  }, [isEclipseSubscribed]);

  // Save settings
  const saveSettings = useCallback(async (updates: Partial<UserSettings> & { subscriptions?: SubscriptionUpdate[] }) => {
    if (!settings) return;

    setSaving(true);
    try {
      const response = await api.updateSettings(updates);
      if (response.success && response.settings) {
        setSettings(response.settings);
        haptic.notification('success');
      } else {
        haptic.notification('error');
        setError(response.error || 'Failed to save settings');
      }
    } catch (err) {
      haptic.notification('error');
      setError(err instanceof Error ? err.message : 'Connection error');
    } finally {
      setSaving(false);
    }
  }, [settings, haptic]);

  // Handle timezone change
  const handleTimezoneChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value || null;
    haptic.selection();
    saveSettings({ timezone: value });
  }, [haptic, saveSettings]);

  // Handle "use primary chart timezone" button
  const handleUsePrimaryChartTimezone = useCallback(() => {
    if (settings?.primaryChartTimezone) {
      haptic.impact('light');
      saveSettings({ timezone: settings.primaryChartTimezone });
    }
  }, [settings, haptic, saveSettings]);

  // Handle notification hour change
  const handleNotificationHourChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = parseInt(e.target.value, 10);
    haptic.selection();
    saveSettings({ notificationHour: value });
  }, [haptic, saveSettings]);

  // Handle forecast toggle
  const handleForecastToggle = useCallback(() => {
    if (!settings) return;
    haptic.impact('light');
    saveSettings({ forecastEnabled: !settings.forecastEnabled });
  }, [settings, haptic, saveSettings]);

  // Handle event notifications toggle
  const handleEventNotificationsToggle = useCallback(() => {
    if (!settings) return;
    haptic.impact('light');
    saveSettings({ eventNotificationsEnabled: !settings.eventNotificationsEnabled });
  }, [settings, haptic, saveSettings]);

  // Handle days before change
  const handleDaysBeforeChange = useCallback((days: number) => {
    haptic.selection();
    saveSettings({ eventDaysBefore: days });
  }, [haptic, saveSettings]);

  // Handle planet retrograde toggle
  const handlePlanetToggle = useCallback((planetKey: string) => {
    haptic.impact('light');
    const isCurrentlySubscribed = isPlanetSubscribed(planetKey);
    const newEnabled = !isCurrentlySubscribed;

    // Toggle both start and end events
    const subscriptions: SubscriptionUpdate[] = [
      { eventType: `${planetKey}_retrograde_start`, enabled: newEnabled },
      { eventType: `${planetKey}_retrograde_end`, enabled: newEnabled },
    ];

    saveSettings({ subscriptions });
  }, [haptic, isPlanetSubscribed, saveSettings]);

  // Handle eclipse toggle
  const handleEclipseToggle = useCallback((eclipseKey: string) => {
    haptic.impact('light');
    const isCurrentlySubscribed = isEclipseSubscribed(eclipseKey);

    const subscriptions: SubscriptionUpdate[] = [
      { eventType: eclipseKey, enabled: !isCurrentlySubscribed },
    ];

    saveSettings({ subscriptions });
  }, [haptic, isEclipseSubscribed, saveSettings]);

  // Toggle retrogrades section
  const toggleRetrogrades = useCallback(() => {
    haptic.selection();
    setRetrogradesExpanded((prev) => !prev);
  }, [haptic]);

  // Toggle eclipses section
  const toggleEclipses = useCallback(() => {
    haptic.selection();
    setEclipsesExpanded((prev) => !prev);
  }, [haptic]);

  // Format hour for display
  const formatHour = (hour: number) => `${hour.toString().padStart(2, '0')}:00`;

  // Generate hour options
  const hourOptions = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label: formatHour(i),
  }));

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <div className={styles.spinner} />
          <p>Загрузка настроек...</p>
        </div>
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <p className={styles.errorText}>{error}</p>
          <button className={styles.retryButton} onClick={() => window.location.reload()}>
            Повторить
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Настройки</h1>

      {/* Error banner */}
      {error && (
        <div className={styles.errorBanner} onClick={() => setError(null)}>
          {error}
        </div>
      )}

      {/* Timezone Section */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Часовой пояс</h2>

        <div className={styles.field}>
          <label className={styles.label}>Текущий пояс</label>
          <select
            className={styles.select}
            value={settings?.timezone || ''}
            onChange={handleTimezoneChange}
            disabled={saving}
          >
            <option value="">Не выбран</option>
            {timezones.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>

        {settings?.primaryChartTimezone && settings.primaryChartTimezone !== settings.timezone && (
          <button
            className={styles.secondaryButton}
            onClick={handleUsePrimaryChartTimezone}
            disabled={saving}
          >
            Определить по основной карте
          </button>
        )}
      </section>

      {/* Daily Forecast Section */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Ежедневный прогноз</h2>

        <div className={styles.toggleRow}>
          <span className={styles.toggleLabel}>Получать прогноз</span>
          <button
            className={`${styles.toggle} ${settings?.forecastEnabled ? styles.toggleOn : ''}`}
            onClick={handleForecastToggle}
            disabled={saving}
            aria-checked={settings?.forecastEnabled}
            role="switch"
          >
            <span className={styles.toggleKnob} />
          </button>
        </div>

        {settings?.forecastEnabled && (
          <div className={styles.field}>
            <label className={styles.label}>Время отправки</label>
            <select
              className={styles.select}
              value={settings?.notificationHour || 9}
              onChange={handleNotificationHourChange}
              disabled={saving}
            >
              {hourOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </section>

      {/* Event Notifications Section */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Уведомления о событиях</h2>
        <p className={styles.sectionHint}>
          Ретрограды планет, затмения и другие важные астрособытия
        </p>

        <div className={styles.toggleRow}>
          <span className={styles.toggleLabel}>Уведомления о событиях</span>
          <button
            className={`${styles.toggle} ${settings?.eventNotificationsEnabled ? styles.toggleOn : ''}`}
            onClick={handleEventNotificationsToggle}
            disabled={saving}
            aria-checked={settings?.eventNotificationsEnabled}
            role="switch"
          >
            <span className={styles.toggleKnob} />
          </button>
        </div>

        {settings?.eventNotificationsEnabled && (
          <>
            <div className={styles.field}>
              <label className={styles.label}>За сколько дней уведомлять</label>
              <div className={styles.segmentedControl}>
                {[1, 2, 3].map((days) => (
                  <button
                    key={days}
                    className={`${styles.segment} ${settings?.eventDaysBefore === days ? styles.segmentActive : ''}`}
                    onClick={() => handleDaysBeforeChange(days)}
                    disabled={saving}
                  >
                    {days} {days === 1 ? 'день' : days < 5 ? 'дня' : 'дней'}
                  </button>
                ))}
              </div>
            </div>

            {/* Retrogrades Category */}
            <div className={styles.category}>
              <button
                className={styles.categoryHeader}
                onClick={toggleRetrogrades}
                disabled={saving}
              >
                <span className={styles.categoryTitle}>
                  <span className={`${styles.chevron} ${retrogradesExpanded ? styles.chevronExpanded : ''}`}>
                    ▶
                  </span>
                  Ретрограды
                  {retrogradesCount > 0 && (
                    <span className={styles.categoryCount}>({retrogradesCount})</span>
                  )}
                </span>
              </button>

              {retrogradesExpanded && (
                <div className={styles.categoryContent}>
                  {RETROGRADE_PLANETS.map((planet) => (
                    <div key={planet.key} className={styles.subscriptionRow}>
                      <span className={styles.subscriptionLabel}>
                        <span className={styles.subscriptionSymbol}>{planet.symbol}</span>
                        {planet.name}
                      </span>
                      <button
                        className={`${styles.toggle} ${styles.toggleSmall} ${isPlanetSubscribed(planet.key) ? styles.toggleOn : ''}`}
                        onClick={() => handlePlanetToggle(planet.key)}
                        disabled={saving}
                        aria-checked={isPlanetSubscribed(planet.key)}
                        role="switch"
                      >
                        <span className={styles.toggleKnob} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Eclipses Category */}
            <div className={styles.category}>
              <button
                className={styles.categoryHeader}
                onClick={toggleEclipses}
                disabled={saving}
              >
                <span className={styles.categoryTitle}>
                  <span className={`${styles.chevron} ${eclipsesExpanded ? styles.chevronExpanded : ''}`}>
                    ▶
                  </span>
                  Затмения
                  {eclipsesCount > 0 && (
                    <span className={styles.categoryCount}>({eclipsesCount})</span>
                  )}
                </span>
              </button>

              {eclipsesExpanded && (
                <div className={styles.categoryContent}>
                  {ECLIPSE_TYPES.map((eclipse) => (
                    <div key={eclipse.key} className={styles.subscriptionRow}>
                      <span className={styles.subscriptionLabel}>
                        <span className={styles.subscriptionSymbol}>{eclipse.symbol}</span>
                        {eclipse.name}
                      </span>
                      <button
                        className={`${styles.toggle} ${styles.toggleSmall} ${isEclipseSubscribed(eclipse.key) ? styles.toggleOn : ''}`}
                        onClick={() => handleEclipseToggle(eclipse.key)}
                        disabled={saving}
                        aria-checked={isEclipseSubscribed(eclipse.key)}
                        role="switch"
                      >
                        <span className={styles.toggleKnob} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <p className={styles.infoText}>
              Уведомления приходят в {formatHour(settings?.notificationHour || 9)} по вашему времени
            </p>
          </>
        )}
      </section>

      {/* Saving indicator */}
      {saving && (
        <div className={styles.savingOverlay}>
          <div className={styles.spinner} />
        </div>
      )}
    </div>
  );
}

export default Settings;
